import { useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import { announce } from '@/app/announcer';
import { useEnvironment } from '@/app/environment';
import { personaHome, useSession } from '@/app/session';
import { Wordmark } from '@/components/shell/Wordmark';
import { buttonVariants } from '@/components/ui/button';
import { SEP } from '@/components/shell/FooterLine';

const ERRORS: Record<string, string> = {
  callback: 'Sign-in did not complete. Try again.',
  state: 'The sign-in took longer than five minutes, or was started in another tab. Start again.',
  persona: 'This account has no Beam persona. Ask an administrator to add you to a group.',
};

/**
 * Unauthenticated: the wordmark, one ink button to the identity provider,
 * and a line that MFA is handled there. The deep link is preserved through
 * the round trip in `next`. Provider error text is never shown.
 */
export function LoginPage() {
  const { status, me } = useSession();
  const [params] = useSearchParams();
  const env = useEnvironment();
  const next = params.get('next') ?? '/';
  const error = params.get('error');
  useEffect(() => announce('Sign in'), []);

  if (status === 'authenticated' && me) return <Navigate to={next.startsWith('/') && next !== '/' ? next : personaHome(me.persona)} replace />;

  const safeNext = next.startsWith('/') ? next : '/';
  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 px-4">
      <main id="content" className="flex w-full max-w-sm flex-col gap-6 rounded-xl border border-beam-line bg-beam-paper p-8">
        <Wordmark to="/login" tone="ink" />
        <div className="flex flex-col gap-2">
          <h1 className="font-heading text-[22px] leading-[1.25] font-medium md:text-[26px]">Sign in to the Beam portal</h1>
          <p className="text-beam-text-muted">Multi-factor authentication is handled by the identity provider.</p>
        </div>
        {error ? (
          <p role="alert" className="rounded-md border border-beam-line border-l-[3px] border-l-beam-error bg-beam-paper px-3 py-2 text-[13px] font-medium">
            {ERRORS[error] ?? ERRORS.callback}
          </p>
        ) : null}
        <a href={`/auth/login?next=${encodeURIComponent(safeNext)}`} className={buttonVariants({ size: 'lg', className: 'h-11 w-full text-base md:h-9 md:text-sm' })}>
          {error && error !== 'persona' ? 'Try again' : 'Sign in'}
        </a>
      </main>
      <p className="tabular text-xs text-beam-text-muted">
        {env.data ? [env.data.region, env.data.env, `build ${env.data.build}`].join(SEP) : ' '}
      </p>
    </div>
  );
}
